"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { ImageOff, Loader2, Lock } from "lucide-react";
import { AppHeader } from "@/components/layout/AppHeader";
import { LegalFooter } from "@/components/LegalFooter";
import { useAuth } from "@/components/auth/AuthProvider";
import { canViewShowcase } from "@/lib/showcase/visibility";
import { ShowcaseCard } from "@/components/showcase/ShowcaseCard";
import { ShowcaseFilters, type ShowcaseSort } from "@/components/showcase/ShowcaseFilters";
import { useShowcaseList } from "@/hooks/useShowcaseApi";

export default function Showcase() {
  const { isAdmin } = useAuth();
  const canView = canViewShowcase(isAdmin);
  const [sort, setSort] = useState<ShowcaseSort>("newest");

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useShowcaseList({
    sort,
    enabled: canView,
  });

  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  // Infinite scroll sentinel.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: "600px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="lb-page min-h-screen">
      <AppHeader />
      <main className="lb-container py-12 sm:py-16">
        <header className="mb-8 flex flex-col gap-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
            Community Showcase
          </p>
          <h1 className="lb-display text-3xl font-semibold tracking-[-0.03em] text-foreground sm:text-[2.6rem]">
            Made in The Workshop
          </h1>
          <p className="max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
            Made something good? Publish it from the apps.
          </p>
        </header>

        {!canView ? (
          <PrivatePreview />
        ) : (
        <>
        <div className="mb-8">
          <ShowcaseFilters sort={sort} onSortChange={setSort} />
        </div>

        {isLoading ? (
          <SkeletonGrid />
        ) : isError ? (
          <EmptyState
            icon={<ImageOff className="h-7 w-7" />}
            title="Couldn't load the showcase"
            subtitle="Please refresh and try again."
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<ImageOff className="h-7 w-7" />}
            title="Nothing here yet"
            subtitle="No published work matches this filter. Be the first to share something."
          />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {items.map((item) => (
                <ShowcaseCard key={item.id} item={item} />
              ))}
            </div>
            <div ref={sentinelRef} className="h-12" />
            {isFetchingNextPage ? (
              <div className="flex justify-center py-6 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : null}
          </>
        )}
        </>
        )}
      </main>
      <LegalFooter />
    </div>
  );
}

function PrivatePreview() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border/60 py-20 text-center">
      <span className="grid h-14 w-14 place-items-center rounded-2xl border border-border/60 bg-card/60 text-muted-foreground">
        <Lock className="h-7 w-7" />
      </span>
      <p className="text-base font-semibold text-foreground">Coming soon</p>
      <p className="max-w-sm text-sm text-muted-foreground">
        The Community Showcase is in private preview. It&apos;ll open to everyone shortly.
      </p>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-2xl border border-border/60 bg-card/60">
          <div className="aspect-square w-full animate-pulse bg-muted" />
          <div className="space-y-2 p-3">
            <div className="h-3.5 w-2/3 animate-pulse rounded bg-muted" />
            <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border/60 py-20 text-center">
      <span className="grid h-14 w-14 place-items-center rounded-2xl border border-border/60 bg-card/60 text-muted-foreground">
        {icon}
      </span>
      <p className="text-base font-semibold text-foreground">{title}</p>
      <p className="max-w-sm text-sm text-muted-foreground">{subtitle}</p>
    </div>
  );
}
